/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import clsx from "clsx";
import "../../../assets/styles/custom-style.css";
import { useAuth } from "../../../auth";
import { addChefBankDetail, getBankDetailsByRoutingNumber } from "../core/_requests";
import { useNavigate } from "react-router-dom";
import ListSettlements from "./ListSettlements";
import { settlementSchema } from "../core/Schema";

interface BankAdd {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  chefId: string | undefined;
  bankId: string | undefined;
  routingNumber: string;
  accountName: string;
  nickName: string;
  accountNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  ssnLast4: string;
  createdBy: any;
  updatedBy: any;
}

const initialValues: BankAdd = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  dobDay: "",
  dobMonth: "",
  dobYear: "",
  chefId: "",
  bankId: "",
  routingNumber: "",
  accountName: "",
  nickName: "",
  accountNumber: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  ssnLast4: "",
  createdBy: "",
  updatedBy: "",
};

const AddSettlements = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [routingNumberError, setRoutingNumberError] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const chefId = currentUser?.uuid;

  const formik = useFormik<BankAdd>({
    initialValues: {
      ...initialValues,
      email: currentUser?.email || "",
    },
    validationSchema: settlementSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const payload = {
        ...values,
        chefId,
        createdBy: currentUser?.chefId,
        dobDay: parseInt(values.dobDay),
        dobMonth: parseInt(values.dobMonth),
        dobYear: parseInt(values.dobYear),
      };

      setSubmissionError("");
      addChefBankDetail(payload).then((res) => {
        if (res?.status === true) {
          navigate("/settlements/success", { replace: true });
        } else {
          const msg = res?.message || "Internal server error";
          setSubmissionError(msg.replace(/Stripe /gi, "").replace(/stripe/gi, "Account"));
        }
        setLoading(false);
      }).catch((err) => {
        console.error(err);
        const errorMsg = err.response?.data?.message || err.message || "Internal server error";
        setSubmissionError(errorMsg.replace(/Stripe /gi, "").replace(/stripe/gi, "Account"));
        setLoading(false);
      });
    },
  });

  const fetchBankDetails = async () => {
    if (!formik.values.routingNumber) return;
    setLoading(true);
    try {
      const res = await getBankDetailsByRoutingNumber(formik.values.routingNumber);
      if (res?.status === "success") {
        formik.setValues({
          ...formik.values,
          bankId: res.data.name,
          city: res.data.city || formik.values.city,
          state: res.data.state || formik.values.state,
          zipCode: res.data.zip || formik.values.zipCode,
        });
        setRoutingNumberError("");
      } else {
        setRoutingNumberError("Invalid routing number");
      }
    } catch (error) {
      setRoutingNumberError("Error fetching bank details");
    }
    setLoading(false);
  };

  const validateStep = async (step: number) => {
    const fields: (keyof BankAdd)[][] = [
      ["firstName", "lastName", "email", "phoneNumber", "dobDay", "dobMonth", "dobYear"],
      ["routingNumber", "accountNumber", "accountName", "bankId", "nickName"],
      ["ssnLast4", "address", "city", "state", "zipCode"]
    ];

    const currentFields = fields[step - 1];

    // Mark fields as touched
    const touched: any = {};
    currentFields.forEach(field => touched[field] = true);
    formik.setTouched({ ...formik.touched, ...touched });

    const errors = await formik.validateForm();
    const hasError = currentFields.some(field => !!errors[field]);

    return !hasError;
  };

  const handleNext = async () => {
    if (await validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => setCurrentStep(prev => prev - 1);

  return (
    <section className="container-fluid addContainerFluid">
      <ListSettlements />
      <div className="overlay"></div>
      <div className="drawer wizard-drawer">
        <div className="wizard-header">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h3 className="wizard-title mb-1">Frayo Onboarding</h3>
              <p className="wizard-subtitle">Complete 3 simple steps to start receiving payouts safely</p>
            </div>
            <button className="btn-close-wizard" onClick={() => navigate("/settlements")}>
              <i className="material-symbols-outlined">close</i>
            </button>
          </div>

          <div className="modern-stepper mb-5">
            <div className={clsx("step-item", { active: currentStep >= 1, completed: currentStep > 1 })}>
              <div className="step-icon"><i className="material-symbols-outlined">person</i></div>
              <span className="step-label">Personal</span>
            </div>
            <div className="step-connector"></div>
            <div className={clsx("step-item", { active: currentStep >= 2, completed: currentStep > 2 })}>
              <div className="step-icon"><i className="material-symbols-outlined">account_balance</i></div>
              <span className="step-label">Bank</span>
            </div>
            <div className="step-connector"></div>
            <div className={clsx("step-item", { active: currentStep >= 3, completed: currentStep > 3 })}>
              <div className="step-icon"><i className="material-symbols-outlined">location_on</i></div>
              <span className="step-label">Identity</span>
            </div>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} noValidate className="wizard-form-container">
          {/* STEP 1: PERSONAL */}
          {currentStep === 1 && (
            <div className="wizard-content animate-fade-in">
              <div className="info-card mb-4">
                <i className="material-symbols-outlined text-primary">security</i>
                <span>Your information is encrypted securely. We follow strict data protection standards to keep your info safe.</span>
              </div>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="form-group-custom">
                    <label>First Name</label>
                    <input type="text" {...formik.getFieldProps("firstName")} className={clsx("form-control-modern", { "error": formik.touched.firstName && formik.errors.firstName })} placeholder="Legal first name" />
                    {formik.touched.firstName && formik.errors.firstName && <div className="error-msg">{formik.errors.firstName}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group-custom">
                    <label>Last Name</label>
                    <input type="text" {...formik.getFieldProps("lastName")} className={clsx("form-control-modern", { "error": formik.touched.lastName && formik.errors.lastName })} placeholder="Legal last name" />
                    {formik.touched.lastName && formik.errors.lastName && <div className="error-msg">{formik.errors.lastName}</div>}
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group-custom">
                    <label>Email Address</label>
                    <input type="email" {...formik.getFieldProps("email")} className={clsx("form-control-modern", { "error": formik.touched.email && formik.errors.email })} placeholder="email@example.com" />
                    {formik.touched.email && formik.errors.email && <div className="error-msg">{formik.errors.email}</div>}
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group-custom">
                    <label>Phone Number</label>
                    <input type="text" {...formik.getFieldProps("phoneNumber")} className={clsx("form-control-modern", { "error": formik.touched.phoneNumber && formik.errors.phoneNumber })} placeholder="Phone (10-15 digits)" />
                    {formik.touched.phoneNumber && formik.errors.phoneNumber && <div className="error-msg">{formik.errors.phoneNumber}</div>}
                  </div>
                </div>
                <div className="col-12">
                  <label className="mb-2 fw-600 fs-14">Date of Birth</label>
                  <div className="row g-2">
                    <div className="col-4">
                      <input type="number" {...formik.getFieldProps("dobDay")} className={clsx("form-control-modern", { "error": formik.touched.dobDay && formik.errors.dobDay })} placeholder="Day" />
                    </div>
                    <div className="col-4">
                      <input type="number" {...formik.getFieldProps("dobMonth")} className={clsx("form-control-modern", { "error": formik.touched.dobMonth && formik.errors.dobMonth })} placeholder="Month" />
                    </div>
                    <div className="col-4">
                      <input type="number" {...formik.getFieldProps("dobYear")} className={clsx("form-control-modern", { "error": formik.touched.dobYear && formik.errors.dobYear })} placeholder="Year" />
                    </div>
                  </div>
                  {(formik.errors.dobDay || formik.errors.dobMonth || formik.errors.dobYear) && <div className="error-msg">Please enter a valid birthday (18+)</div>}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: BANK */}
          {currentStep === 2 && (
            <div className="wizard-content animate-fade-in">
              <div className="row g-4">
                <div className="col-12">
                  <div className="form-group-custom">
                    <label>Routing Number</label>
                    <div className="input-with-action">
                      <input type="text" {...formik.getFieldProps("routingNumber")} className={clsx("form-control-modern", { "error": formik.touched.routingNumber && (formik.errors.routingNumber || routingNumberError) })} placeholder="9-digit routing number" />
                      <button type="button" onClick={fetchBankDetails} className="btn-input-action">
                        {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="material-symbols-outlined">search</i>}
                      </button>
                    </div>
                    {(formik.errors.routingNumber || routingNumberError) && <div className="error-msg">{formik.errors.routingNumber || routingNumberError}</div>}
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group-custom">
                    <label>Account Number</label>
                    <input type="password" {...formik.getFieldProps("accountNumber")} className={clsx("form-control-modern", { "error": formik.touched.accountNumber && formik.errors.accountNumber })} placeholder="Enter account number" />
                    {formik.touched.accountNumber && formik.errors.accountNumber && <div className="error-msg">{formik.errors.accountNumber}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group-custom">
                    <label>Bank Name</label>
                    <input type="text" {...formik.getFieldProps("bankId")} className={clsx("form-control-modern", { "error": formik.touched.bankId && formik.errors.bankId })} placeholder="e.g. Chase Bank" />
                    {formik.touched.bankId && formik.errors.bankId && <div className="error-msg">{formik.errors.bankId}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group-custom">
                    <label>Account Holder Name</label>
                    <input type="text" {...formik.getFieldProps("accountName")} className={clsx("form-control-modern", { "error": formik.touched.accountName && formik.errors.accountName })} placeholder="As shown on statement" />
                    {formik.touched.accountName && formik.errors.accountName && <div className="error-msg">{formik.errors.accountName}</div>}
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group-custom">
                    <label>Internal Nickname</label>
                    <input type="text" {...formik.getFieldProps("nickName")} className={clsx("form-control-modern", { "error": formik.touched.nickName && formik.errors.nickName })} placeholder="e.g. Primary Payouts" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: IDENTITY & ADDRESS */}
          {currentStep === 3 && (
            <div className="wizard-content animate-fade-in">
              <div className="row g-4">
                <div className="col-12">
                  <div className="form-group-custom">
                    <label>Last 4 digits of SSN</label>
                    <input type="text" maxLength={4} {...formik.getFieldProps("ssnLast4")} className={clsx("form-control-modern", { "error": formik.touched.ssnLast4 && formik.errors.ssnLast4 })} placeholder="XXXX" />
                    <small className="text-muted mt-1 d-block">Required for identity verification</small>
                    {formik.touched.ssnLast4 && formik.errors.ssnLast4 && <div className="error-msg">{formik.errors.ssnLast4}</div>}
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group-custom">
                    <label>Street Address</label>
                    <input type="text" {...formik.getFieldProps("address")} className={clsx("form-control-modern", { "error": formik.touched.address && formik.errors.address })} placeholder="Residential address" />
                    {formik.touched.address && formik.errors.address && <div className="error-msg">{formik.errors.address}</div>}
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="form-group-custom">
                    <label>City</label>
                    <input type="text" {...formik.getFieldProps("city")} className={clsx("form-control-modern", { "error": formik.touched.city && formik.errors.city })} placeholder="City" />
                    {formik.touched.city && formik.errors.city && <div className="error-msg">{formik.errors.city}</div>}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group-custom">
                    <label>State</label>
                    <input type="text" {...formik.getFieldProps("state")} className={clsx("form-control-modern", { "error": formik.touched.state && formik.errors.state })} placeholder="State" />
                    {formik.touched.state && formik.errors.state && <div className="error-msg">{formik.errors.state}</div>}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group-custom">
                    <label>Zip Code</label>
                    <input type="text" {...formik.getFieldProps("zipCode")} className={clsx("form-control-modern", { "error": formik.touched.zipCode && formik.errors.zipCode })} placeholder="Zip" />
                    {formik.touched.zipCode && formik.errors.zipCode && <div className="error-msg">{formik.errors.zipCode}</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {submissionError && (
            <div className="submission-error-pill mt-4 animate-fade-in">
              <div className="d-flex align-items-center gap-3 w-100">
                <i className="material-symbols-outlined">warning</i>
                <div className="flex-grow-1">
                  <span className="d-block fw-700">{submissionError}</span>
                  {submissionError.toLowerCase().includes("exists") && (
                    <small className="d-block mt-1 opacity-75">You have already completed your onboarding process.</small>
                  )}
                </div>
                {submissionError.toLowerCase().includes("exists") && (
                  <button
                    type="button"
                    onClick={() => navigate("/settlements")}
                    className="btn btn-sm btn-light-success py-1 px-3 fw-bold"
                  >
                    Go to Wallet
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="wizard-footer mt-4">
            <div className="d-flex justify-content-between">
              {currentStep > 1 ? (
                <button type="button" onClick={handleBack} className="btn-wizard-back">
                  <i className="material-symbols-outlined">arrow_back</i>
                  <span>Back</span>
                </button>
              ) : <div></div>}

              {currentStep < 3 ? (
                <button type="button" onClick={handleNext} className="btn-wizard-next">
                  <span>Continue</span>
                  <i className="material-symbols-outlined">arrow_forward</i>
                </button>
              ) : (
                <button type="submit" disabled={loading || !formik.isValid} className="btn-wizard-finish">
                  {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="material-symbols-outlined me-2">check_circle</i>}
                  Complete Onboarding
                </button>
              )}
            </div>
          </div>
        </form>

        <style>{`
          .wizard-drawer { 
            background: #fff !important; 
            padding: 2.5rem !important; 
            border-radius: 20px 0 0 20px !important; 
            box-shadow: -10px 0 30px rgba(0,0,0,0.05) !important;
            width: 50% !important; /* Reduced width to not cover full screen */
            max-width: 600px !important;
            height: 100vh !important;
            overflow-y: auto !important;
          }
          
          @media (max-width: 768px) {
            .wizard-drawer { width: 90% !important; }
          }

          .wizard-title { font-weight: 800; color: #1e293b; letter-spacing: -0.5px; }
          .wizard-subtitle { color: #64748b; font-size: 14px; }
          .btn-close-wizard { background: #f1f5f9; border: none; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; transition: 0.3s; }
          .btn-close-wizard:hover { background: #e2e8f0; transform: rotate(90deg); }

          .modern-stepper { display: flex; align-items: center; justify-content: space-between; position: relative; padding: 0 10px; }
          .step-item { display: flex; flex-direction: column; align-items: center; gap: 6px; z-index: 2; position: relative; }
          .step-icon { width: 40px; height: 40px; border-radius: 12px; background: #f1f5f9; color: #94a3b8; display: flex; align-items: center; justify-content: center; transition: 0.4s; }
          .step-label { font-size: 11px; font-weight: 700; color: #94a3b8; letter-spacing: 0.5px; text-transform: uppercase; }
          .step-item.active .step-icon { background: #198754; color: #fff; transform: scale(1.05); }
          .step-item.active .step-label { color: #198754; }
          .step-item.completed .step-icon { background: #ecfdf5; color: #198754; }
          .step-connector { height: 2px; flex-grow: 1; background: #f1f5f9; margin-bottom: 20px; transition: 0.4s; }

          .info-card { background: #f0fdf4; border-left: 4px solid #198754; padding: 1rem; border-radius: 8px; display: flex; align-items: center; gap: 12px; font-size: 13px; color: #166534; line-height: 1.4; }
          .form-group-custom label { display: block; font-size: 13px; font-weight: 700; color: #334155; margin-bottom: 6px; }
          .form-control-modern { width: 100%; padding: 10px 14px; border-radius: 10px; border: 2px solid #e2e8f0; transition: 0.3s; font-size: 14px; }
          .form-control-modern:focus { border-color: #198754; outline: none; box-shadow: 0 0 0 3px rgba(25, 135, 84, 0.1); }
          .form-control-modern.error { border-color: #ef4444; }
          .error-msg { color: #ef4444; font-size: 12px; margin-top: 4px; font-weight: 500; }

          .input-with-action { position: relative; }
          .btn-input-action { position: absolute; right: 6px; top: 6px; bottom: 6px; background: #198754; color: #fff; border: none; border-radius: 8px; padding: 0 10px; display: flex; align-items: center; }

          .wizard-footer { border-top: 1px solid #f1f5f9; padding-top: 1.5rem; }
          .btn-wizard-next, .btn-wizard-finish { background: #198754; color: #fff; border: none; padding: 10px 24px; border-radius: 12px; font-weight: 700; display: flex; align-items: center; gap: 8px; transition: 0.3s; }
          .btn-wizard-next:hover, .btn-wizard-finish:hover { background: #157347; transform: translateY(-1px); }
          .btn-wizard-back { background: #fff; color: #64748b; border: 2px solid #e2e8f0; padding: 10px 20px; border-radius: 12px; font-weight: 700; display: flex; align-items: center; gap: 8px; }

          .animate-fade-in { animation: fadeIn 0.3s ease-out; }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
          
          .fw-600 { font-weight: 600; }
          .fs-14 { font-size: 14px; }
          .wizard-form-container { max-width: 100%; }

          .submission-error-pill {
            background: #fef2f2;
            border: 1px solid #fee2e2;
            color: #b91c1c;
            padding: 12px 16px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 13px;
            font-weight: 600;
          }
          .submission-error-pill i { font-size: 22px; }
          .btn-light-success {
            background: #d1fae5;
            color: #065f46;
            border: none;
            transition: 0.2s;
          }
          .btn-light-success:hover {
            background: #198754;
            color: #fff;
          }
        `}</style>
      </div>
    </section>
  );
};

export default AddSettlements;
